import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";

interface Brand {
  id: number;
  name: string;
  image_url?: string;
}

const BrandsScroll: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("brands")
          .select("id, name, image_url")
          .eq("is_hidden", false)
          .limit(20);

        if (error) throw error;

        data.forEach((brand: Brand) => {
          if (brand.image_url) {
            brand.image_url = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/publicimages/brand-images/${brand.image_url}`;
          }
        });

        if (data && data.length > 0) {
          setBrands([...data, ...data, ...data]);
        }
      } catch (err) {
        console.error("Error fetching brands:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const handleBrandClick = (brandId: number) => {
    navigate(`/products?brand_id=${brandId}`);
  };

  if (loading) {
    return (
      <div className="py-5 sm:py-8 overflow-hidden bg-[#f0eee9] border-y border-[#e0e0e0]">

        <div className="flex gap-3 sm:gap-4 px-4 sm:px-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="shrink-0 w-32.5 h-16 sm:w-50 sm:h-22 bg-white border border-[#e0e0e0] animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!brands.length) return null;

  const BrandCard = ({ brand, index }: { brand: Brand; index: number }) => (
    <div
      key={`${brand.id}-${index}`}
      onClick={() => handleBrandClick(brand.id)}
      className="
        group/card shrink-0 cursor-pointer
        w-32.5 h-16 sm:w-50 sm:h-22
        flex items-center justify-center
        px-4
        bg-white
        border border-[#e0e0e0]
        hover:border-[#c9a96e]
        transition-colors duration-300
      "
    >
      {brand.image_url ? (
        <img
          src={brand.image_url}
          alt={brand.name}
          className="
            max-h-9 sm:max-h-12 max-w-full w-auto h-auto object-contain
            grayscale opacity-75
            transition-[filter,opacity] duration-300
            group-hover/card:grayscale-0 group-hover/card:opacity-100
          "
        />
      ) : (
        <span
          className="
            font-serif uppercase tracking-[0.15em] whitespace-nowrap
            text-sm sm:text-base text-[#1a1614]
            opacity-70 group-hover/card:opacity-100
            transition-opacity duration-300
          "
        >
          {brand.name}
        </span>
      )}
    </div>
  );

  return (
    <div className="py-5 sm:py-8 overflow-hidden bg-[#f0eee9] border-y border-[#e0e0e0] relative">
      {/* Edge fade masks */}
      <div className="absolute inset-y-0 left-0 w-10 sm:w-30 bg-linear-to-r from-[#f0eee9] to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-10 sm:w-30 bg-linear-to-l from-[#f0eee9] to-transparent z-10 pointer-events-none" />

      <div className="brands-scroll-track">
        {brands.map((brand, i) => (
          <BrandCard key={`${brand.id}-${i}`} brand={brand} index={i} />
        ))}
      </div>
    </div>
  );
};

export { BrandsScroll };
